import { useChallengePlayer } from '@infl-ts/util-use-challenge-player';
import CloseIcon from '@mui/icons-material/Close';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Drawer, IconButton, Tab, Typography } from '@mui/material';
import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/function';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../tooltip';
import ActivityAction from './activity-action';
import CompletedActivityCard from './complete-activity-card';
import InProgressActivityCard from './in-progress-activity-card';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var isInProgress = function isInProgress(activity) {
  return activity.state !== 'closed' && activity.state !== 'rejected' && activity.state !== 'dismissed';
};

var isClosed = function isClosed(activity) {
  return activity.state === 'closed' || activity.state === 'rejected';
};

export var DESKTOP_DRAWER_WIDTH = 410;
export var MOBILE_DRAWER_WIDTH = 350;
export var ActivitySelector = function ActivitySelector(_ref) {
  var activities = _ref.activities,
      challenge = _ref.challenge,
      isOpen = _ref.isOpen,
      closeDrawer = _ref.closeDrawer,
      container = _ref.container;

  var _useState = useState(challenge.availableForCompletion ? 'inProgress' : 'completed'),
      currentTab = _useState[0],
      setCurrentTab = _useState[1];

  var _useChallengePlayer = useChallengePlayer(),
      currentActivity = _useChallengePlayer.challengePlayerState.activity;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/_jsx(Drawer, {
    sx: [{
      zIndex: function zIndex(t) {
        return t.zIndex._3;
      },
      flexShrink: 0,
      flexGrow: {
        xs: 1,
        md: 'inherit'
      },
      width: 0,
      transition: function transition(theme) {
        return theme.transitions.create(['width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        });
      }
    }, isOpen && {
      width: {
        xs: '100%',
        md: MOBILE_DRAWER_WIDTH,
        lg: DESKTOP_DRAWER_WIDTH
      },
      transition: function transition(theme) {
        return theme.transitions.create(['width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        });
      }
    }],
    PaperProps: container !== undefined ? {
      style: {
        position: 'initial'
      }
    } : undefined,
    ModalProps: container !== undefined ? {
      container: container,
      style: {
        position: 'absolute'
      }
    } : undefined,
    variant: "persistent",
    anchor: "right",
    open: isOpen,
    "data-element": "Drawer",
    "data-component": "ActivitySelector",
    "data-source-file": "index.tsx",
    children: /*#__PURE__*/_jsxs(TabContext, {
      value: currentTab,
      "data-element": "TabContext",
      "data-component": "ActivitySelector",
      "data-source-file": "index.tsx",
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          mx: 2,
          my: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        },
        "data-element": "Box",
        "data-component": "ActivitySelector",
        "data-source-file": "index.tsx",
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "h4",
          sx: {
            display: 'flex',
            alignItems: 'center'
          },
          "data-element": "Typography",
          "data-component": "ActivitySelector",
          "data-source-file": "index.tsx",
          children: t('Your Submissions for this Challenge')
        }), /*#__PURE__*/_jsx(Tooltip, {
          title: t('Close'),
          "data-element": "Tooltip",
          "data-source-file": "index.tsx",
          children: /*#__PURE__*/_jsx(IconButton, {
            sx: {
              height: 40,
              width: 40
            },
            onClick: closeDrawer,
            "data-element": "IconButton",
            "data-source-file": "index.tsx",
            children: /*#__PURE__*/_jsx(CloseIcon, {
              sx: {
                height: 20,
                width: 20
              },
              "data-element": "CloseIcon",
              "data-source-file": "index.tsx"
            })
          })
        })]
      }), /*#__PURE__*/_jsxs(TabList, {
        centered: true,
        sx: {
          display: 'flex',
          width: '100%'
        },
        onChange: function onChange(_event, newValue) {
          return setCurrentTab(newValue);
        },
        "data-element": "TabList",
        "data-source-file": "index.tsx",
        children: [challenge.availableForCompletion ? /*#__PURE__*/_jsx(Tab, {
          sx: {
            width: '50%'
          },
          label: t('In progress'),
          value: "inProgress"
        }) : null, /*#__PURE__*/_jsx(Tab, {
          sx: {
            width: challenge.availableForCompletion ? '50%' : '100%'
          },
          label: t('Completed'),
          value: "completed",
          "data-element": "Tab",
          "data-source-file": "index.tsx"
        })]
      }), /*#__PURE__*/_jsx(TabPanel, {
        sx: {
          p: 0,
          height: 'inherit'
        },
        value: "inProgress",
        "data-element": "TabPanel",
        "data-source-file": "index.tsx",
        children: /*#__PURE__*/_jsxs(Box, {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: 1
          },
          "data-element": "Box",
          "data-source-file": "index.tsx",
          children: [/*#__PURE__*/_jsx(Box, {
            sx: {
              width: 1
            },
            "data-element": "Box",
            "data-source-file": "index.tsx",
            children: pipe(activities, A.filter(isInProgress), A.reverse, A.map(function (activity) {
              return /*#__PURE__*/_jsx(InProgressActivityCard, {
                activity: activity,
                closeDrawer: closeDrawer,
                isSelected: (currentActivity === null || currentActivity === void 0 ? void 0 : currentActivity.id) === activity.id,
                challenge: challenge
              }, activity.id);
            }))
          }), challenge.availableForCompletion && /*#__PURE__*/_jsx(ActivityAction, {
            challenge: challenge,
            closeDrawer: closeDrawer
          })]
        })
      }), /*#__PURE__*/_jsx(TabPanel, {
        sx: {
          p: 0
        },
        value: "completed",
        "data-element": "TabPanel",
        "data-source-file": "index.tsx",
        children: pipe(activities, A.filter(isClosed), A.reverse, A.map(function (activity) {
          var _activity$pointsEarne;

          return /*#__PURE__*/_jsx(CompletedActivityCard, {
            activity: activity,
            isSelected: (currentActivity === null || currentActivity === void 0 ? void 0 : currentActivity.id) === activity.id,
            pointsEarned: (_activity$pointsEarne = activity.pointsEarned) !== null && _activity$pointsEarne !== void 0 ? _activity$pointsEarne : 0
          }, activity.id);
        }))
      })]
    })
  });
};